/**
 * The two surfaces the APP owns — as opposed to the rows an owner makes.
 *
 * The owner (2026-10-31): *"we said we gonna make dashboard system defined.. why
 * is it still under layouts.. also make system static 3d page"*. Both were
 * ordinary rows that a system URL happened to point at: `/dashboard` only
 * redirected to `/layouts/<id>`, and the 3D page was one more row the generic
 * canvas view rendered. This module names them, so a route, a menu and a list
 * can ask one question and get one answer.
 *
 * They are still ROWS — the dashboard's cards are the owner's, and the 3D page
 * keeps its named viewpoints — but they are neither a layout nor somebody's own
 * page: every list hides them, and each has exactly one URL.
 */
import { isLayoutDashboard } from './layoutMarker';
import { wireObject } from './wire';

export type SystemPageKind = 'dashboard' | 'model';

/** The key the hub writes on the page a wide screen lands on. */
export const SYSTEM_DASHBOARD_KEY = 'desktopDefault';

/** Each system page's own path. A scoped hub prefixes it (`/h/<hub>/dashboard`). */
export const SYSTEM_PAGE_PATHS: Record<SystemPageKind, string> = {
  dashboard: '/dashboard',
  model: '/model',
};

export interface SystemPageCandidate {
  layoutConfig?: unknown;
  hubPage?: boolean | null;
}

/** The page a wide screen lands on — the hub marks the row when it seeds one. */
export function isSystemDashboardRow(row: SystemPageCandidate | null | undefined): boolean {
  if (!row || isLayoutDashboard(row)) return false;
  return wireObject(row.layoutConfig)?.[SYSTEM_DASHBOARD_KEY] === true;
}

/** The hub's shared 3D page — one row for the whole install (`t_hub_bim_model`). */
export function isSystemModelRow(row: SystemPageCandidate | null | undefined): boolean {
  return row?.hubPage === true;
}

/** Which system page this row IS, or undefined when it is an owner's own row. */
export function systemPageKindOfRow(row: SystemPageCandidate | null | undefined): SystemPageKind | undefined {
  if (isSystemDashboardRow(row)) return 'dashboard';
  if (isSystemModelRow(row)) return 'model';
  return undefined;
}

/** May a page list, a layout list or a landing offer this row? */
export function isSystemPageRow(row: SystemPageCandidate | null | undefined): boolean {
  return systemPageKindOfRow(row) !== undefined;
}

/** The row behind `kind`, or undefined before the hub has one. */
export function pickSystemPage<T extends SystemPageCandidate>(
  rows: readonly T[],
  kind: SystemPageKind,
): T | undefined {
  return rows.find((row) => systemPageKindOfRow(row) === kind);
}

/** `kind`'s own path, scoped when the route names a hub. */
export function systemPagePath(kind: SystemPageKind, hubId?: string | null): string {
  const path = SYSTEM_PAGE_PATHS[kind];
  return hubId ? `/h/${hubId}${path}` : path;
}
